'use client';

import { Listbox, Transition } from '@headlessui/react';
import { CustomFlowbiteTheme, TextInput } from 'flowbite-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { BiCheck, BiFilter, } from "react-icons/bi";
import { HiOutlineChevronUpDown } from "react-icons/hi2";
import MultiFallbackImage from 'app/MultiFallbackImage';
import { FilterableListItem, getItemThumbnailUrls, getItemName, getItemDescription } from './FilterableListbox-utils';

const filterInputTheme: CustomFlowbiteTheme['textInput'] = {
    field: {
        base: 'relative w-full mx-4 rounded-none',
        input: {
            withIcon: {
                on: 'pl-10 rounded-none border-b dark:bg-black bg-white border-neutral-400  dark:border-neutral-600  h-8 my-2',
                off: 'rounded-none'
            },
            withAddon: {
                on: 'rounded-none',
                off: 'rounded-none'
            }
        }
    }
}

type FilterableListboxProps = {
    options: FilterableListItem[]
    name: string
    value: string
    disabled: boolean
    hideFilter?: boolean
    onChange: (value: string) => void
};


export default function FilterableListbox({ name, options, onChange, disabled, value, hideFilter }: FilterableListboxProps) {
    const [selected, setSelected] = useState(value)
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const filtered = query === ''
        ? options
        : options.filter((item: FilterableListItem) => {
            const filterableString = typeof item === 'string' ? item : (item.name + ': ' + item.description??'')
            return filterableString.toLowerCase()
                .replace(/\s+/g, '')
                .includes(query.toLowerCase().replace(/\s+/g, ''))
        })
    

    // Support case where value is changed externally
    useEffect(() => {
        setSelected(value)
    }, [value])

    // Take keyboard focus on update
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus()
            }, 50) 
        }
    }, [isOpen])

    return (
        <Listbox
            value={selected}
            onChange={(newValue) => {
                onChange(newValue);
                setSelected(newValue);
                setIsOpen(false);                
            }}
            name={name}
            disabled={disabled}
        >
            {({ open }) => {
                if (open !== isOpen) {
                    setIsOpen(open)
                }
                return <>
                    <div className="relative w-full cursor-default overflow-hidden text-left bg-white border border-neutral-700  dark:bg-black dark:border-neutral-400 focus-visible:ring-blue-500/35 focus:ring-blue-500/35">
                        <Listbox.Button className="w-full h-11 flex cursor-pointer items-center justify-between px-2 focus:outline-none  bg-white border-neutral-700  dark:bg-black dark:border-neutral-400 py-1 focus-visible:ring-blue-500/35 focus:ring-blue-500/35">
                            <span className="block truncate text-neutral-800 dark:text-neutral-300">
                                {selected}
                            </span>
                            <HiOutlineChevronUpDown
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </Listbox.Button>
                    </div>

                    <Transition
                        as={Fragment}
                        show={open}
                        enterFrom='opacity-0'
                        enter='transition ease-in duration-100'
                        enterTo='opacity-100'
                        leave="transition ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Listbox.Options className="absolute border max-h-80 lg:max-h-96 overflow-y-scroll rounded-none bg-white border-neutral-700  dark:bg-black dark:border-neutral-400 text-base sm:text-sm z-30 max-w-80">
                            {!hideFilter && <TextInput
                                ref={inputRef}
                                autoFocus={open}
                                icon={BiFilter}
                                theme={filterInputTheme}
                                sizing="sm"
                                type='search'
                                onChange={(event) => setQuery(event.target.value)}
                                value={query}
                                placeholder='filter'
                                className='sticky top-0 z-10 rounded-none bg-white dark:bg-black  border-neutral-700  dark:border-neutral-400 p-2'
                            />}
                            {filtered.length === 0 && query !== '' ? (
                                <div className="relative cursor-default select-none px-4 py-2 text-neutral-700 dark:text-neutral-300">
                                    Nothing found.
                                </div>
                            ) : 
                                filtered.map((option) => {
                                    const urls = getItemThumbnailUrls(option);
                                    const optionName = getItemName(option);
                                    const description = getItemDescription(option);
                                    return <Listbox.Option
                                        key={optionName}
                                        className={({ active }) => `relative border-t border-neutral-100 dark:border-neutral-800  cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-black text-white dark:bg-white dark:text-black' : 'text-neutral-800 dark:text-neutral-300'}`}
                                        value={optionName}>
                                        {({ selected, active }) => (<>
                                            <div className='flex flex-row justify-between items-center' >
                                                <div className='mr-2 w-max'>
                                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                        {optionName}
                                                    </span>
                                                    {description &&  <span className='text-xs text-neutral-500 dark:text-neutral-400  break-words overflow-hidden'>{description}</span>}
                                                </div>
                                                {selected  && <span
                                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'dark:text-black text-white' : 'dark:text-white text-black'}`}>
                                                        <BiCheck className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                }
                                                {urls && (urls.length>0) && <MultiFallbackImage sources={urls} alt={optionName} width={64}  height={64} />}
                                            </div>
                                        </>)}
                                    </Listbox.Option>
                                })
                            }
                        </Listbox.Options>
                    </Transition>
                </>
            }}
        </Listbox >

    )
}
